/* eslint-disable jsdoc/require-description */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.

 ** WIKI for analytics events
 ** https://wiki.corp.adobe.com/pages/viewpage.action?spaceKey=ccs&title=Analytics+events+from+CCS+UI
 **************************************************************************
*/

import AnalyticsUtil from './AnalyticsUtil';
import data from '../../translations/en.json';
import GMOData from '../utils/GMOMarketing';
import { getLogger } from '../utils/SearchLogger';
import EventsService from '../services/EventsService';
import WebStorage from '../storage/WebStorage';
import SearchHostContext from '../utils/SearchHostContext';
import {
	SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS,
	SEARCH_TABS_TO_LOCALIZED_STRINGS_MAP,
} from '../containers/SearchResultsPage/SearchConstants';
import SearchConfig from '../utils/SearchConfig';
import SearchUtils from '../utils/SearchUtils';

let logMetricsCollection = false;
const suggestion = 'suggestion';

type PreSearchAnalytics = {
	recommendationType: string;
	data: Array<Record<string, any>>;
	eventType?: string;
	fromModel?: boolean;
	isExpress?: boolean;
	planGroup?: Array<string>;
	modelVersion?: string;
	uiSortOrder?: number;
	eventIndex?: number;
	sendUISearchKeyword?: boolean;
	eventContentType?: string;
};

export default class SearchAnalytics {
	searchHostContext: HostContext;
	static prompt: string;
	static keyword: string;
	static sliceValue: number;
	static contextGuid: any;
	static setContextGuid: boolean;
	static currentImage: Record<string, any>;
	static searchHistoryLen: number;
	static requestId: string;
	static dropDownData: Record<string, any> = {};
	static tabTitle: string;
	static cleared: boolean;
	static searchType: string;
	static history: string;
	static autoComplete: string;
	static currentSearch: string;
	static clickCount: Record<string, number> = {};
	ingest: Record<string, any>;
	analyticsUtils: Record<string, any>;
	logger: Logger;
	static pagination: { label: string };

	constructor(searchHostContext: HostContext) {
		this.searchHostContext = searchHostContext;
		this.analyticsUtils = new AnalyticsUtil(searchHostContext);
		this.ingest = this.analyticsUtils.ingest;
		this.logger = getLogger();
		logMetricsCollection = false;

		if (window.logLearnMetrics && window.logLearnMetrics > 0) {
			logMetricsCollection = true;
		}
	}

	/**
	 * [initAnalytics Init Ingest]
	 * @returns {[none]}
	 */
	initAnalytics() {
		this.ingest.initializeDefaultData();
		this.setSearchPrompt();
		this.setKeyWord();
		this.setSearchType();
	}

	setKeyWord() {
		if (typeof SearchAnalytics.keyword !== 'undefined' && !SearchAnalytics.keyword.length) {
			const userSearchData = SearchAnalytics.getSearchDataForUser();
			SearchAnalytics.keyword = userSearchData.keyword;
		}
	}

	setSearchType() {
		if (typeof SearchAnalytics.searchType !== 'undefined' && !SearchAnalytics.searchType.length) {
			const userSearchData = SearchAnalytics.getSearchDataForUser();
			SearchAnalytics.searchType = userSearchData.searchType;
		}
	}

	/**
	 * Gets the viewType based on the route in which the user did a search
	 */
	getViewType() {
		const hostUIState = this.searchHostContext.getHostUIState();
		return (hostUIState && hostUIState.tab) || '';
	}

	/**
	 * Set the key word with the search query
	 * @param {*} keyword
	 */
	initSearchAnalytics(keyword: string) {
		SearchAnalytics.keyword = keyword;
		this.setSearchType();
		SearchAnalytics.saveSearchData();
	}

	/**
	 * Set the prompt id
	 */
	setSearchPrompt() {
		/* eslint-disable */
		SearchAnalytics.prompt = data[AnalyticsUtil.searchPromptId];
		/* eslint-enable */
	}

	/**
	 * [sendToIngest Post to ingest]
	 * @returns {[none]}
	 */
	sendToIngest() {
		if (typeof this.ingest.metricsCollection !== 'undefined' && this.ingest.metricsCollection.length) {
			this.ingest.sendToIngest();

			if (logMetricsCollection) {
				const arrayLength = this.ingest.metricsCollection.length;
				this.logger.log(
					'Search Analytics',
					'metricsCollection.length = ',
					this.ingest.metricsCollection.length,
				);

				for (let i = 0; i < arrayLength; i++) {
					this.logger.log(
						'Search Analytics',
						'sendToIngest metricsCollection = ',
						this.ingest.metricsCollection[i].events[0].data,
					);
				}
			}
		}
	}

	/**
	 * [isEventDataValid checks to see if specified event exists and is of the correct type]
	 * @returns {[boolean]}
	 */
	isEventDataValid(eventData: Record<string, any>, event: string) {
		return (
			eventData &&
			eventData.events &&
			eventData.events[0] &&
			eventData.events[0].data &&
			eventData.events[0].data[event] &&
			typeof eventData.events[0].data[event] === 'string'
		);
	}

	/**
	 * [sendToIntent sends cleansed data to intent server]
	 * @returns {[none]}
	 */
	sendToIntent(ingestCollection: Array<Record<string, any>>) {
		// clone ingestCollection
		const cloneIngestCollection = JSON.parse(JSON.stringify(ingestCollection));
		const sensitiveEventsData = ['content.name', 'content.id', 'content.action'];
		const sensitiveContentCategories = [
			'Lightroom',
			'Files',
			'Adobe Spark projects',
			'Mobile creations',
			'Cloud documents',
			'Libraries',
			'Prototypes & Specs',
		];
		// logic in this block is to replace sensitive user values with
		// empty strings to avoid GDPR
		cloneIngestCollection.forEach((eventData) => {
			const contentCategory = this.isEventDataValid(eventData, 'content.category')
				? eventData.events[0].data['content.category']
				: '';

			// if contentCategory matches any of the categories in sensitiveContentCategories
			// set each item in sensitiveEventsData to empty strings
			if (sensitiveContentCategories.indexOf(contentCategory) > -1) {
				sensitiveEventsData.forEach((event) => {
					if (this.isEventDataValid(eventData, event)) {
						eventData.events[0].data[event] = '';
					}
				});
			}
		});
		const eventsService = new EventsService(this.searchHostContext);
		eventsService.reportEvents(cloneIngestCollection);
	}

	/**
	 *
	 * @param category
	 * @param actionUrl
	 * @returns void
	 */
	postSeeAll(category: string, actionUrl?: string, trackingData?: Record<string, any> | null): void {
		this.initAnalytics();
		const subCategory = `Details:${this.getSubCategory()}`;
		const data = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setEventSubtype: 'see-all',
			setContentCategory: category,
			setContentAction: actionUrl,
			...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
		};
		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} action
	 * @param {*} project
	 */
	postModalAction(category: string, action: string, project: any, url: string) {
		this.initAnalytics();
		SearchAnalytics.currentImage = project;
		const subCategory = `Details:${this.getSubCategory()}`;
		const data = {
			setEventSubcategory: subCategory,
			setEventType: action,
			setEventSubtype: 'detail-page',
			setContentID: project.id,
			setContentName: project.attributes.name,
			setContentCategory: category,
			setContentAction: url,
			setEventValue: this.getSearchType(),
		};
		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 * Handle clicking the arrow buttons in the Carousel
	 */
	postCarouselClick(e: any, category: string): void {
		this.initAnalytics();
		let direction;

		if (e.type === 'LEFT_BUTTON_CLICKED') {
			direction = 'scroll-left';
		}

		if (e.type === 'RIGHT_BUTTON_CLICKED') {
			direction = 'scroll-right';
		}

		const subCategory = `Details:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setEventSubtype: direction,
			setContentCategory: category,
			setEventValue: this.getSearchType(),
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	// FIXME: @babbula could you check to see if uiSortOrder here should be optional or not?
	postClickOnAppsResult({
		category,
		appTitle,
		appData,
		eventSubtype,
		uiSequence,
		uiSortOrder,
		eventVal,
		contentActionURL,
		exactTypedSearchQuery,
	}: {
		category: string;
		appTitle: string;
		appData: Record<string, any>;
		eventSubtype: string;
		uiSequence: number;
		uiSortOrder?: number;
		eventVal?: string;
		contentActionURL?: string;
		exactTypedSearchQuery?: string;
	}): void {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const trackingData = appData?.metadata?.tracking || null;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: eventVal,
			setEventType: 'click',
			setContentAction: contentActionURL,
			setEventSubtype: eventSubtype,
			setContentCategory: category,
			setContentName: appTitle,
			setContentType: exactTypedSearchQuery,
			setUISequence: uiSequence,
			setUISortOrder: uiSortOrder,
			setUIViewType: category, // setContentID: data.id,
			// setContentAction: data.attributes && data.attributes.openUrl
			...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
		};
		this.addAssetType(dataObject, appData);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postClickOnResult({
		category,
		data,
		url,
		modal,
		action,
		appName,
		autocomplete,
	}: {
		category: string;
		data: any;
		url: string;
		modal?: boolean;
		action?: ActionType;
		appName?: string;
		autocomplete?: boolean;
	}): void {
		this.initAnalytics();
		let dataObject;
		const subCategory = `Results:${this.getSubCategory({ autocomplete })}`;
		const assetCategory = this.transposeAssetCategory(category, data);
		const assetSubCategory = this.getAssetSubCategory(data);

		const assetCategoryAndSubCategory = assetSubCategory ? `${assetCategory} | ${assetSubCategory}` : assetCategory;

		if (modal) {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: action ? action : 'open',
				setEventSubtype: 'result',
				setContentCategory: assetCategoryAndSubCategory,
				setContentID: data.id,
				setContentName: data.attributes.name,
				setContentAction: url,
			};
		}
		if (autocomplete) {
			let analyticsType = 'open';
			if (action) {
				analyticsType = action === 'openIn' ? 'open' : action;
			}
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: analyticsType,
				setEventSubtype: 'result',
				setContentCategory: assetCategoryAndSubCategory,
				setContentID: data.id,
				setContentName: data.title || data.attributes.name,
				setContentAction: appName ? `App|${appName}` : url,
			};
		} else {
			let analyticsType = 'open';
			if (action) {
				analyticsType = action === 'openIn' ? 'open' : action;
			}
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: analyticsType,
				setEventSubtype: 'result',
				setContentCategory: assetCategoryAndSubCategory,
				setContentID: data.id,
				setContentName: data.title || data.attributes.name,
				setContentAction: appName ? `App|${appName}` : url,
			};
		}

		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**  PLUGINS SEARCH ANALYTICS -- START**/
	postPluginorFontResults(category: string, categoryRank: number, plugins: Array<any>): void {
		this.initAnalytics();
		const resultsCount = plugins.length;
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setContentCategory: category,
			setEventCount: resultsCount,
			setUISortOrder: categoryRank,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postRenderOnPluginOrFontCard(
		category: string,
		appTitle: string,
		data: Record<string, any>,
		count: number,
		eventSubtype: string,
		uiSequence: number,
		categoryRank: number,
		exactTypedSearchQuery?: string,
	): void {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setEventCount: count,
			setContentCategory: category,
			setContentName: appTitle,
			setContentType: exactTypedSearchQuery,
			setUISequence: uiSequence,
			setUIViewType: category,
			setUISortOrder: categoryRank,
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postClickOnPluginCardOrFontCard(
		category: string,
		appTitle: string,
		data: Record<string, any>,
		eventSubtype: string,
		uiSequence: number,
		uiSortOrder?: number,
		eventVal?: string,
		contentActionURL?: string,
		exactTypedSearchQuery?: string,
		count?: number,
	): void {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setContentAction: contentActionURL,
			setEventSubtype: eventSubtype === 'plugin' ? 'result' : eventSubtype,
			setContentCategory: category,
			setContentName: appTitle,
			setContentType: exactTypedSearchQuery,
			setUISequence: uiSequence,
			setUISortOrder: uiSortOrder,
			setUIViewType: category,
			setEventValue: this.getSearchType(),
			setEventCount: count,
			// setContentID: data.id,
			// setContentAction: data.attributes && data.attributes.openUrl
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}
	/**  PLUGINS SEARCH ANALYTICS -- END**/

	// SRP QUICK ACTION ANALYTICS -- START
	postQuickActionResults(category: string, categoryRank: number, plugins: Array<any>): void {
		this.initAnalytics();
		const resultsCount = plugins.length;
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setContentCategory: category,
			setEventCount: resultsCount,
			setUISortOrder: categoryRank,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postRenderOnQuickActionCard(
		category: string,
		appTitle: string,
		data: Record<string, any>,
		count: number,
		eventSubtype: string,
		uiSequence: number,
		categoryRank: number,
		exactTypedSearchQuery?: string,
	): void {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setEventCount: count,
			setContentCategory: category,
			setContentName: appTitle,
			setContentType: exactTypedSearchQuery,
			setUISequence: uiSequence,
			setUIViewType: category,
			setUISortOrder: categoryRank,
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postClickOnQuickActionCard(
		category: string,
		quickActionTitle: string,
		data: Record<string, any>,
		uiSequence: number,
		uiSortOrder?: number,
		contentActionURL?: string,
		exactTypedSearchQuery?: string,
		count?: number,
	): void {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setContentAction: contentActionURL,
			setEventSubtype: 'result',
			setContentCategory: category,
			setContentName: quickActionTitle,
			setContentType: exactTypedSearchQuery,
			setUISequence: uiSequence,
			setUISortOrder: uiSortOrder,
			setUIViewType: category,
			setEventValue: this.getSearchType(),
			setEventCount: count,
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}
	// SRP QUICK ACTION ANALYTICS -- END

	postNoResults(category: string, exactTypedSearchQuery: string, _errorInfo = null) {
		this.initAnalytics();

		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'no-result',
			setContentCategory: category,
			setContentType: exactTypedSearchQuery ? exactTypedSearchQuery : _errorInfo,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postNonAttributeResults(category: string, data: any, categoryRank: number) {
		this.initAnalytics();
		const resultsCount = data.length;
		const subCategory = `Results:${this.getSubCategory()}`;
		data.slice(0, SearchAnalytics.sliceValue).forEach((obj, index) => {
			const assetCategory = this.transposeAssetCategory(category, obj);
			const dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setUISearchKeyword: SearchAnalytics.keyword,
				setEventType: 'render',
				setEventSubtype: 'result',
				setContentCategory: assetCategory,
				setContentID: obj.id,
				setContentName: obj.title || (obj.attributes && obj.attributes.name),
				setContentAction: obj.attributes && obj.attributes.openUrl,
				setEventCount: resultsCount,
				setUISequence: index,
				setUISortOrder: categoryRank,
			};
			this.addAssetType(dataObject, obj);
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} category
	 * @param {*} data
	 */
	postAppResults(category: string, data: any, categoryRank: number) {
		this.initAnalytics();
		const resultsCount = data.length;
		const subCategory = `Results:${this.getSubCategory()}`;
		data.forEach((obj, index) => {
			const name = obj && obj.name ? obj.name : '';
			const trackingData = obj?.metadata?.tracking || null;
			const dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'render',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentName: name,
				// Don't have these
				// setContentAction: data.attributes && data.attributes.openUrl,
				// setContentID: obj.id,
				setEventCount: resultsCount,
				setUISequence: index,
				setUISortOrder: categoryRank,
				...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
			};
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} category
	 * @param {*} data
	 */
	postHelpXResults({
		category,
		data,
		categoryRank,
		trackingData,
	}: {
		category: string;
		data: any;
		categoryRank: number;
		trackingData: Record<string, any> | null;
	}): void {
		this.initAnalytics();
		const resultsCount = data.length;
		const subCategory = `Results:${this.getSubCategory()}`;
		data.forEach((obj, index) => {
			const dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'render',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: obj.id,
				setContentName: obj.attributes.name,
				setContentAction: data.attributes && data.attributes.openUrl,
				setEventCount: resultsCount,
				setUISequence: index,
				setUISortOrder: categoryRank,
				...(trackingData && { setContentStatus: trackingData }),
			};
			this.addAssetType(dataObject, obj);
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} category
	 * @param {*} dataArray
	 */
	postSearchResults({
		category,
		data,
		categoryRank,
		autocomplete,
	}: {
		category?: string;
		data: Array<ResultItem>;
		categoryRank?: number;
		autocomplete?: boolean;
	}) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory({ autocomplete })}`;
		const maxEvents = Math.min(data.length, SearchAnalytics.sliceValue);
		Array.isArray(data) &&
			data.slice(0, maxEvents).forEach((obj, index) => {
				let openUrl = '';

				if (obj.attributes && obj.attributes.openUrl) {
					openUrl = obj.attributes.openUrl;
				}

				const assetCategory = this.transposeAssetCategory(category, obj);
				const assetSubCategory = this.getAssetSubCategory(obj);

				const assetCategoryAndSubCategory = assetSubCategory
					? `${assetCategory} | ${assetSubCategory}`
					: assetCategory;
				const dataObject = {
					setEventSubcategory: subCategory,
					setEventValue: this.getSearchType(),
					setEventType: 'render',
					setEventSubtype: 'result',
					setContentCategory: assetCategoryAndSubCategory,
					setContentID: obj.id,
					setContentName: obj.attributes.name,
					setContentAction: openUrl,
					setEventCount: maxEvents,
					setUISequence: index,
					setUISortOrder: categoryRank,
				};
				this.addAssetType(dataObject, obj);
				this.logAnalytics(dataObject);
			});
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} category
	 * @param {*} dataArray
	 * 	FIXME: @babbula could you check to see if uiSortOrder here should be optional or not?
	 */
	postTopResultsRender(category: string, data: Record<string, any>, index?: number) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const contentCategory = `Results:${category}`;
		let name = '';
		let openUrl = '';

		if (category === 'sparkTemplates') {
			name = data.title;
			openUrl = data.attributes && data.attributes.openUrl;
		} else {
			name = data.attributes.name;
			openUrl = data.attributes && data.attributes.openUrl;
		}

		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setContentCategory: contentCategory,
			setContentID: data.id,
			setContentName: name,
			setContentAction: openUrl,
			setUISequence: index,
			setContentType: 'Top Results',
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} category
	 * @param {*} dataArray
	 */
	postTopResultsRenderWithExperiments(
		category: string,
		data: Record<string, any>,
		index: number | null | undefined,
		campaignId: string,
		variationId: string,
	) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const contentCategory = `Results:${category}`;
		let name = '';
		let openUrl = '';

		if (category === 'sparkTemplates') {
			name = data.title;
			openUrl = data.attributes && data.attributes.openUrl;
		} else {
			name = data.attributes.name;
			openUrl = data.attributes && data.attributes.openUrl;
		}

		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'result',
			setContentCategory: contentCategory,
			setContentID: data.id,
			setContentName: name,
			setContentAction: openUrl,
			setUISequence: index,
			setContentType: data.expContentType ? data.expContentType : 'Top Results',
		};
		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postClickOnTopResulteResultExperiment(
		category: string,
		data: any,
		url: string,
		campaignId: string,
		variationId: string,
		modal = false,
	) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		let dataObject;

		if (modal) {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: data.id,
				setContentName: data.attributes.name,
				setContentAction: url,
				setContentType: 'Top Results',
			};
		} else {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentName: data.title || data.attributes.name,
				setContentAction: url,
				setContentType: category === 'knowledge-card' ? 'Top Results App Card' : 'Top Results',
			};
		}

		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 *
	 */
	postSearchBarSubmit() {
		this.initAnalytics();
		let subType = 'search';
		let value = this.getSearchType();

		if (SearchAnalytics.searchType === SearchAnalytics.autoComplete) {
			subType = suggestion;
			value = SearchAnalytics.autoComplete;
		}

		const searchConfig = SearchConfig.getSearchConfig();
		const placeholderIndexSelected = SearchUtils.getPlaceholderIndexInLocalStorage();
		const placeholdersFromConfig = searchConfig && searchConfig.ui && searchConfig.ui.searchBarPlaceholders;

		const dataObject: Record<string, any> = {
			setEventSubcategory: 'Autocomplete',
			setEventValue: value,
			setEventType: 'click',
			setEventSubtype: subType,
		};
		if (placeholdersFromConfig && placeholderIndexSelected) {
			dataObject.setUIPlaceholder = placeholdersFromConfig[placeholderIndexSelected];
		}
		this.logAnalytics(dataObject, false);
		WebStorage.sessionStorage.setItem('delayedEvent', dataObject);
		this.sendToIngest();
	}

	/**
	 * Post when the user delete/removes history item
	 * @param {*} item
	 */
	postDeleteSearchHistoryIem(index: number, item: string) {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'Autocomplete',
			setEventValue: item,
			setEventType: 'remove',
			setEventSubtype: 'suggestion',
			setUISequence: index,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * Post the selection made from search histroy drop down
	 * @param {number} index Index of selection
	 * @param {string} exactTypedSearchQuery The string typed in search bar
	 */
	postDropDownSelection(index: number, exactTypedSearchQuery: string, sourceOfQuery?: string): void {
		this.initAnalytics();
		const isAutoSuggestion = this.isAutoSuggestion();
		const value = isAutoSuggestion ? SearchAnalytics.autoComplete : SearchAnalytics.history;
		const subtype = isAutoSuggestion ? suggestion : 'search';
		const searchConfig = SearchConfig.getSearchConfig();
		const placeholderIndexSelected = SearchUtils.getPlaceholderIndexInLocalStorage();
		const placeholdersFromConfig = searchConfig && searchConfig.ui && searchConfig.ui.searchBarPlaceholders;

		const dataObject: Record<string, any> = {
			setEventSubcategory: 'Autocomplete',
			setEventValue: value,
			setEventType: 'click',
			setEventSubtype: subtype,
			setEventCount: this.getNumberofSearchResults(),
			setUISequence: index,
			setContentType: exactTypedSearchQuery,
			...(sourceOfQuery && { setContentCategory: sourceOfQuery }),
		};

		if (placeholdersFromConfig && placeholderIndexSelected) {
			dataObject.setUIPlaceholder = placeholdersFromConfig[placeholderIndexSelected];
		}
		this.logAnalytics(dataObject, false);
		WebStorage.sessionStorage.setItem('delayedEvent', dataObject);
		this.sendToIngest();
	}

	/**
	 * Report when component loads
	 */
	postSearchComponentLoad(leftNavStatus: boolean) {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'Initialize',
			setEventValue: SearchAnalytics.prompt,
			setEventType: 'init',
			setEventSubtype: 'component',
			setEventCount: SearchAnalytics.searchHistoryLen,
			setContentType: leftNavStatus ? 'leftNav On' : 'leftNav Off',
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * Reports when user started typing
	 */
	postSearchStartedTyping(typedSearchQuery: string, leftNavStatus: boolean) {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'Main',
			setEventValue: this.getSearchType(),
			setEventType: 'startedTyping',
			setEventSubtype: 'input',
			setContentType: leftNavStatus ? 'leftNav On' : 'leftNav Off',
			setUISearchKeyword: typedSearchQuery,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 *
	 */
	postSearchBarClick(subType: string, leftNavStatus: boolean) {
		this.initAnalytics();
		SearchAnalytics.cleared = subType === 'clear';
		const subCategory = subType === 'exit' ? `Results:${this.getSubCategory()}` : 'Main';
		const eventValue = subCategory === 'Main' ? SearchAnalytics.prompt : this.getSearchType();
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventValue: eventValue,
			setEventType: 'click',
			setEventSubtype: subType,
			setEventCount: SearchAnalytics.searchHistoryLen,
			setContentType: leftNavStatus ? 'leftNav On' : 'leftNav Off',
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * When user clicks a card on all results on SRP for topical order experiment
	 * @param {*} category
	 * @param {*} data
	 * @param {*} url
	 * @param {*} modal
	 * @param {*} bucket
	 * @param {*} sequence
	 * FIXME: @babbula could you check to see if uiSequence here should be optional or not?
	 */
	postClickOnResultTopicalOrderExperiment({
		category,
		data,
		url,
		modal,
		uiSequence,
		uiSortOrder,
		trackingData,
	}: {
		category: string;
		data: any;
		url: string;
		modal: boolean;
		uiSequence?: number;
		uiSortOrder: number;
		trackingData?: Record<string, any>;
	}): void {
		this.initAnalytics();
		let dataObject;
		const subCategory = `Results:${this.getSubCategory()}`;

		if (modal) {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: data.id,
				setContentName: data.attributes.name,
				setContentAction: url,
				setContentType: 'Results',
				setUISequence: uiSequence,
				setUISortOrder: uiSortOrder,
				...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
			};
		} else {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: data.id,
				setContentName: data.title || data.attributes.name,
				setContentAction: url,
				setContentType: 'Results',
				setUISequence: uiSequence,
				setUISortOrder: uiSortOrder,
				...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
			};
		}

		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * When user clicks a card Top Result of SRP for topical order experiment
	 * @param {*} category
	 * @param {*} data
	 * @param {*} url
	 * @param {*} modal
	 * @param {*} bucket
	 * @param {*} sequence
	 */
	postClickTopResultTopicalOrderExperiment(
		category: string,
		data: any,
		url: string,
		modal = false,
		sequence: number,
	) {
		this.initAnalytics();
		let dataObject;
		const subCategory = `Results:${this.getSubCategory()}`;

		if (modal) {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: data.id,
				setContentName: data.attributes.name,
				setContentAction: url,
				setContentType: 'Top Results',
				setUISequence: sequence,
				setUISortOrder: sequence + 1,
			};
		} else {
			dataObject = {
				setEventSubcategory: subCategory,
				setEventValue: this.getSearchType(),
				setEventType: 'click',
				setEventSubtype: 'result',
				setContentCategory: category,
				setContentID: data.id,
				setContentName: data.title || data.attributes.name,
				setContentAction: url,
				setContentType: 'Top Results',
				setUISequence: sequence,
				setUISortOrder: sequence + 1,
			};
		}

		this.addAssetType(dataObject, data);
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * When user renders Search page (Top Result)
	 * @param {*} searchQuery
	 * @param {*} uiSequence
	 */
	postSearchCategoryTopResultSequence(searchQuery: string, categoryArray: Array<any>) {
		const subCategory = `Results:${this.getSubCategory()}`;
		this.initAnalytics();
		categoryArray.forEach((cat, index) => {
			let source = '';

			if (cat.source) {
				source = cat.source;
			}

			const dataObject = {
				setEventSubcategory: subCategory,
				setUISequence: index,
				setUISortOrder: index + 1,
				setUIViewType: source,
				setEventType: 'render',
				setEventSubtype: 'result',
				setEventCount: categoryArray.length,
				setEventValue: this.getSearchType(),
				setContentType: 'Top Results',
				setContentCategory: source,
				// setContentID: we need to add this
				// setContentName: we need to add this
				setUISearchKeyword: searchQuery,
			};
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 * When user renders Search page (Blended Top Result)
	 * @param {*} searchQuery
	 * @param {*} uiSequence
	 */
	postSearchCategoryBlendedTopResultSequence(searchQuery: string, categoryArray: Record<string, any>) {
		const subCategory = `Results:${this.getSubCategory()}`;
		this.initAnalytics();
		categoryArray.resultItems.forEach((cat, index) => {
			const dataObject = {
				setEventSubcategory: subCategory,
				setUISequence: index,
				setUISortOrder: index + 1,
				setUIViewType: cat.attributes.ussContext.group,
				setEventType: 'render',
				setEventSubtype: 'result',
				setEventCount: categoryArray.totalResults,
				setEventValue: this.getSearchType(),
				setContentID: cat.id,
				setContentName: cat.attributes.name,
				setContentType: 'Top Results',
				setContentCategory: cat.attributes.ussContext.group,
				setUISearchKeyword: searchQuery,
			};
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 * When user renders Search page
	 * @param {*} action
	 */
	postSearchResultsCategoryOrder(categoryArray: Array<string>, searchQuery: string, modelVersion: string) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		categoryArray.forEach((cat, index) => {
			const dataObject = {
				setEventSubcategory: subCategory,
				setUISortOrder: index + 1,
				setUIViewType: cat,
				setEventType: 'render',
				setEventSubtype: 'result',
				setContentType: 'Results',
				setUISearchKeyword: searchQuery,
				setEventValue: modelVersion,
			};
			this.logAnalytics(dataObject);
		});
		this.ingest.sendToIngest();
	}

	/**
	 *
	 * @param {*} eventType
	 * @param {*} subType
	 * @param {*} value
	 * @param {*} position
	 */
	postSuggestionRender(data: Array<any>, subType: string, value: string) {
		this.initAnalytics();

		// Don't post if we are clearing the
		// search bar
		if (SearchAnalytics.cleared) {
			SearchAnalytics.cleared = true;
			return;
		}

		data.forEach((selected, index) => {
			const dataObject = {
				setEventSubcategory: 'Autocomplete',
				setEventType: 'render',
				setEventSubtype: subType,
				setEventValue: value,
				setUISequence: index,
				...(selected?.source && { setContentCategory: selected?.source }),
			};
			this.logAnalytics(dataObject);
		});
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} eventType
	 * @param {*} subType
	 * @param {*} value
	 * @param {*} position
	 */
	postSuggestionAction(eventType: string, subType: string) {
		this.initAnalytics();
		const isAutoSuggestion = this.isAutoSuggestion();

		if (isAutoSuggestion) {
			const index = this.getAutoSuggestionIndex();
			const dataObject = {
				setEventSubcategory: 'Autocomplete',
				setEventType: eventType,
				setEventSubtype: subType,
				setEventValue: SearchAnalytics.keyword,
				setUISequence: index,
			};
			this.logAnalytics(dataObject);
			this.sendToIngest();
		}
	}

	/**
	 * When user clicks/renders main page
	 * @param {*} action
	 * @param {*} subType
	 * @param {*} exactTypedSearchQuery
	 */
	postSearchResultsPageAction(action: string, subType: string, exactTypedSearchQuery: string) {
		this.initAnalytics();
		const subCategory = `Results:${this.getSubCategory()}`;
		const dataObj = {
			setEventSubcategory: subCategory,
			setEventType: action,
			setEventSubtype: subType,
			setEventValue: this.getSearchType(),
			setContentType: exactTypedSearchQuery,
		};
		this.logAnalytics(dataObj);
		this.ingest.sendToIngest();

		if (typeof this.ingest.metricsCollection !== 'undefined' && this.ingest.metricsCollection.length) {
			const eventsService = new EventsService(this.searchHostContext);
			eventsService.reportEvents(this.ingest.metricsCollection);
		}

		const gmoData = new GMOData(this.searchHostContext);
		// gmoData.dunamisToGMOData(this.ingest.metricsCollection, action);
	}

	/**
	 *
	 * @param {*} feedbackType
	 * @param {*} feedbackText
	 * @param {*} searchQuery
	 * @param {*} searchResultsDataTotalCount
	 */
	postThumbsFeedbackTextSubmit(
		feedbackType: string,
		feedbackText: string,
		searchQuery: string,
		searchResultsDataTotalCount: number,
	) {
		this.initAnalytics();
		const subCategory = `Feedback:${this.getSubCategory()}`;
		const data = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setEventSubtype: 'feedback',
			setContentType: 'comment',
			setContentName: feedbackType,
			setEventValue: feedbackText,
			setEventCount: searchResultsDataTotalCount,
			setUISearchKeyword: searchQuery,
		};
		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} feedbackType
	 * @param {*} searchQuery
	 * @param {*} searchResultsDataTotalCount
	 */
	postThumbsFeedbackButtonClick(feedbackType: string, searchQuery: string, searchResultsDataTotalCount: number) {
		this.initAnalytics();
		const subCategory = `Feedback:${this.getSubCategory()}`;
		const data = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setEventSubtype: 'feedback',
			setContentType: 'icon',
			setContentName: feedbackType,
			setEventCount: searchResultsDataTotalCount,
			setUISearchKeyword: searchQuery,
		};
		this.logAnalytics(data);
		this.sendToIngest();
	}

	/**
	 *
	 * @param {*} subCategory
	 * @param {*} sortOrder
	 */
	postCategoryRender(categoryArray: any) {
		this.initAnalytics();
		categoryArray.forEach((ele, index) => {
			const data = {
				setEventSubcategory: `Details:${this.getSubCategory()}`,
				setEventType: 'render',
				setEventSubtype: 'category',
				setUISortOrder: index + 1,
				//  we are 1 basing
				setContentCategory: ele,
			};
			this.logAnalytics(data);
			this.sendToIngest();
		});
	}

	/*************************************************************************
	 * Quick Links Experiment Analytics
	 **************************************************************************/

	/**
	 *
	 * @param {*} itemsArray
	 * @param {string} subType
	 * @param {string} value
	 * @param {string} query
	 */
	postQuickLinksRender(
		app: Record<string, any>,
		subType: string,
		value: string,
		query: string,
		appTitle: string,
		index: number,
	) {
		this.initAnalytics();
		// Don't post if we are clearing the
		// search bar
		const quickLinksCount =
			SearchAnalytics.dropDownData != null && SearchAnalytics.dropDownData.quickLinks != null
				? SearchAnalytics.dropDownData.quickLinks.length
				: 0;

		if (SearchAnalytics.cleared) {
			SearchAnalytics.cleared = true;
			return;
		}

		const dataObject = {
			setEventSubcategory: 'Autocomplete',
			setEventType: 'render',
			setContentCategory: 'Apps',
			setContentName: appTitle,
			setEventSubtype: subType,
			setEventValue: value,
			setUIViewType: this.getViewType(),
			setEventCount: quickLinksCount,
			setContentAction: app.url,
			setUISearchKeyword: query,
			setUISortOrder: 1,
			setUISequence: index,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * Post the selection made from quick links.
	 * @param {string} appTitle
	 * @param {string} subType
	 * @param {string} inAppSource
	 * @param {string} typedSearchQuery
	 */
	postLaunchOfApp(appTitle: string, url: any, subType: string, inAppSource: string, typedSearchQuery: string): void {
		this.initAnalytics();

		let subCategory = 'Autocomplete';
		if (inAppSource === SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.SEARCH_RESULT) {
			subCategory = `Results:${this.getSubCategory()}`;
		}
		const dataObject = {
			setEventSubcategory: subCategory,
			setEventType: 'click',
			setContentCategory: 'Apps',
			setContentName: appTitle,
			setContentAction: url,
			setEventSubtype: subType,
			setUIViewType: this.getViewType(),
			setUISearchKeyword: appTitle,
			setContentType: typedSearchQuery,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	/**
	 * Post the app is launched from SRP
	 * @param {string} appTitle
	 * @param {string} subType
	 * @param {string} executedSearchQuery
	 * @param {string} typedSearchQuery
	 */
	postLaunchOfAppFromSRP({
		appTitle,
		url,
		subType,
		executedSearchQuery,
		typedSearchQuery,
		appData,
	}: {
		appTitle: string;
		url: any;
		subType: string;
		executedSearchQuery: string;
		typedSearchQuery: string;
		appData: Record<string, any>;
	}): void {
		this.initAnalytics();
		const trackingData = appData?.metadata?.tracking || null;
		const dataObject = {
			setEventSubcategory: `Results:${this.getSubCategory()}`,
			setEventType: 'click',
			setContentCategory: 'Apps',
			setContentName: appTitle,
			setContentAction: url,
			setEventSubtype: subType,
			setUIViewType: this.getViewType(),
			setUISearchKeyword: executedSearchQuery,
			setContentType: typedSearchQuery,
			...(trackingData && { setContentStatus: JSON.stringify(trackingData) }),
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	/**
	 * Post the selection made from quick links.
	 * @param {Object} appResultItem
	 * @param {number} index Index of selection
	 * @param {string} subType
	 * @param {string} value
	 * @param {string} typedSearchQuery
	 */
	postQuickLinksDropDownSelection(
		appName: string,
		url: string,
		index: number,
		subType: string,
		value: string,
		typedSearchQuery: string,
	) {
		this.initAnalytics();
		const quickLinksCount =
			SearchAnalytics.dropDownData != null && SearchAnalytics.dropDownData.quickLinks != null
				? SearchAnalytics.dropDownData.quickLinks.length
				: 0;
		const searchConfig = SearchConfig.getSearchConfig();
		const placeholderIndexSelected = SearchUtils.getPlaceholderIndexInLocalStorage();
		const placeholdersFromConfig = searchConfig && searchConfig.ui && searchConfig.ui.searchBarPlaceholders;
		const appTitle = appName;
		const dataObject: Record<string, any> = {
			setEventSubcategory: 'Autocomplete',
			setEventType: 'click',
			setContentCategory: 'Apps',
			setContentName: appTitle,
			setEventSubtype: subType,
			setEventValue: value,
			setEventCount: quickLinksCount,
			setUIViewType: this.getViewType(),
			setUISearchKeyword: appTitle,
			setContentAction: url,
			setUISortOrder: 1,
			setUISequence: index,
			setContentType: typedSearchQuery,
		};

		if (placeholdersFromConfig && placeholderIndexSelected) {
			dataObject.setUIPlaceholder = placeholdersFromConfig[placeholderIndexSelected];
		}
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	/********************************
	 * Analytics for Intent to Action Shortcuts
	 ********************************/

	postIntentShortcutRender(
		app: Record<string, any>,
		subType: string,
		query: string,
		appTitle: string,
		index: number,
		count: number,
		contentType: string,
	): void {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'Shortcut',
			setEventType: 'render',
			setContentCategory: app.actionType,
			setContentName: appTitle,
			setEventSubtype: subType,
			setEventValue: app.id,
			setUIViewType: this.getViewType(),
			setEventCount: count,
			setContentAction: app.url,
			setUISearchKeyword: query,
			setUISortOrder: 1,
			setUISequence: index,
			setContentType: contentType,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postIntentShortcutSelection(
		app: Record<string, any>,
		subType: string,
		query: string,
		appTitle: string,
		index: number,
		count: number,
		contentType: string,
	): void {
		this.initAnalytics();

		if (SearchAnalytics.cleared) {
			SearchAnalytics.cleared = true;
			return;
		}

		const dataObject = {
			setEventSubcategory: 'Shortcut',
			setEventType: 'click',
			setContentCategory: app.actionType,
			setContentName: appTitle,
			setEventSubtype: subType,
			setEventValue: app.id,
			setUIViewType: this.getViewType(),
			setEventCount: count,
			setContentAction: app.analyticsUrl,
			setUISearchKeyword: query,
			setUISortOrder: 1,
			setUISequence: index,
			setContentType: contentType,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	// Dropdown files - start
	postDropdownFileRender({
		category,
		file,
		index,
		eventValue,
	}: {
		category: string;
		file: ResultItem;
		index: number;
		eventValue: string;
	}): void {
		this.initAnalytics();
		const assetCategory = this.transposeAssetCategory(file.attributes.assetCategory, file);
		const assetSubCategory = this.getAssetSubCategory(file);
		const assetCategoryAndSubCategory = assetSubCategory ? `${assetCategory} | ${assetSubCategory}` : assetCategory;
		const dataObject = {
			setEventSubcategory: category,
			setEventType: 'render',
			setContentCategory: assetCategoryAndSubCategory,
			setContentName: file.attributes?.name,
			setEventSubtype: 'content',
			setEventValue: eventValue,
			setContentAction: file.attributes?.landingSurface ?? file.attributes?.openUrl,
			setUISearchKeyword: SearchAnalytics.keyword,
			setUISortOrder: 0,
			setUISequence: index,
			setContentType: file.attributes?.mimeType,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postDropdownFileSelection({
		category,
		file,
		index,
		eventValue,
	}: {
		category: string;
		file: ResultItem;
		index: number;
		eventValue: string;
	}): void {
		this.initAnalytics();
		const assetCategory = this.transposeAssetCategory(file.attributes.assetCategory, file);
		const assetSubCategory = this.getAssetSubCategory(file);
		const assetCategoryAndSubCategory = assetSubCategory ? `${assetCategory} | ${assetSubCategory}` : assetCategory;
		const dataObject = {
			setEventSubcategory: category,
			setEventType: 'open',
			setContentCategory: assetCategoryAndSubCategory,
			setContentName: file.attributes?.name,
			setEventSubtype: 'result',
			setEventValue: eventValue,
			setContentAction: file.attributes?.landingSurface ?? file.attributes?.openUrl,
			setUISearchKeyword: SearchAnalytics.keyword,
			setUISortOrder: 0,
			setUISequence: index,
			setContentType: file.attributes?.mimeType,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	// Dropdown files - end

	/**
	 * Sends analytics for placeholder test rendered
	 * @param placeHolderText The actual placeholder text to be rendered
	 */
	postSearchBarPlaceholderRender(placeHolderId: string): void {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'search',
			setEventValue: this.getSearchType(),
			setEventType: 'render',
			setEventSubtype: 'searchPlaceholder',
			setUIPlaceholder: placeHolderId,
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	postPreSearchItemAction({
		recommendationType,
		eventType,
		fromModel,
		isExpress,
		planGroup,
		modelVersion,
		uiSortOrder,
		data,
		eventIndex,
		sendUISearchKeyword = false,
		eventContentType,
	}: PreSearchAnalytics): void {
		this.initAnalytics();
		Array.isArray(data) &&
			data.forEach((obj, index) => {
				// this is used by quicklinks, apps, expressTemplates , QA, recentFiles in presearch
				// all have action url in diffrent properties
				const actionUrl = obj.actionUrl || obj?.attributes?.openUrl || obj?.action?.url;
				let contentType = eventContentType ?? 'static';
				if (fromModel) {
					if (index === 0) {
						if (isExpress) {
							contentType = `dynamic-${modelVersion}`;
						}
					} else {
						contentType = `dynamic-${modelVersion}`;
					}
				}
				const dataObject = {
					setEventSubcategory: 'preSearchRecommendation',
					setEventType: eventType ?? 'click',
					setContentCategory: recommendationType,
					setContentName: obj?.id,
					setEventSubtype: 'content',
					setContentAction: actionUrl,
					setUISequence: eventIndex ?? index,
					setContentType: contentType,
					...(planGroup && { setEventValue: planGroup }),
					...(uiSortOrder && { setUISortOrder: uiSortOrder }),
					...(data.length && { setEventCount: data.length }),
					// This is being sent from the leo search suggestion and needs to be separated from here
					...(obj.name && sendUISearchKeyword && { setUISearchKeyword: obj.name }),
				};
				this.logAnalytics(dataObject);
			});
		this.sendToIngest();
	}

	postPreSearchCarouselScroll(e: any, category: string): void {
		this.initAnalytics();
		let direction;

		if (e.type === 'LEFT_BUTTON_CLICKED') {
			direction = 'scroll-left';
		}

		if (e.type === 'RIGHT_BUTTON_CLICKED') {
			direction = 'scroll-right';
		}

		const dataObject = {
			setEventSubcategory: 'preSearchRecommendation',
			setEventType: 'click',
			setEventSubtype: direction,
			setContentCategory: category,
			setEventValue: this.getSearchType(),
		};
		this.logAnalytics(dataObject);
		this.sendToIngest();
	}

	/**
	 * ***************************************
	 * END : Analytics for pre search quick actions
	 * ***************************************
	 */

	/**
	 * Send the event when selected filters are cleared
	 */
	postClearFilters(): void {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'filters',
			setEventType: 'click',
			setContentCategory: 'clear',
			setEventSubtype: 'search',
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	/**
	 * Send the event when selected filters are applied
	 */
	postApplyFilters(selectedUIFilters: SelectedUiFilters): void {
		let selectedFilters = '';
		Object.keys(selectedUIFilters).forEach((key) => {
			if (selectedFilters !== '') {
				selectedFilters += '|';
			}
			if (Array.isArray(selectedUIFilters[key])) {
				selectedFilters += selectedUIFilters[key].join('|');
			} else {
				selectedFilters += `${selectedUIFilters[key]}`;
			}
		});
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'filters',
			setEventType: 'click',
			setContentCategory: 'apply',
			setEventSubtype: 'search',
			setContentName: selectedFilters,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	postProductInformationClick({
		categoryRank,
		resultRank,
		category,
		resultItem,
	}: {
		resultRank: number;
		categoryRank: number;
		category: string;
		resultItem: Record<string, any>;
	}): void {
		this.initAnalytics();
		const dataObject = {
			setEventType: 'click',
			setUISortOrder: categoryRank,
			setUISequence: resultRank,
			setContentAction: resultItem.attributes.openUrl || '',
			setContentCategory: category,
			setEventSubtype: 'result',
			setContentID: resultItem.id,
			setEventSubcategory: `Results:${this.getSubCategory()}`,
			setContentName: resultItem.attributes.name,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	postLeoAnalytics(data: Array<Record<string, any>> | Record<string, any>): void {
		const dataArray = Array.isArray(data) ? data : [data];
		this.initAnalytics();
		dataArray.forEach((obj) => this.logAnalytics(obj));
		this.sendToIngest();
	}

	postGenAIToggle({ toggleValue }: { toggleValue: boolean }): void {
		this.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'Results:All',
			setEventType: 'click',
			setEventSubtype: 'leoToggle',
			setContentCategory: 'Leo Response Card Toggle',
			setContentAction: `${!toggleValue}|${toggleValue}`,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	postPaginationRenderAnalytics(data: { page: number; categoryRank: number; category: string }): void {
		this.initAnalytics();
		const dataObject = {
			setEventType: 'render',
			setUISortOrder: data.categoryRank,
			setContentCategory: data.category,
			setEventSubtype: 'view-more-results',
			setEventSubcategory: `Results:${this.getSubCategory()}`,
			setEventValue: SearchAnalytics.pagination.label,
			setEventCount: data.page,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}

	postPaginationClickAnalytics(data: { page: number; categoryRank: number; category: string }): void {
		this.initAnalytics();
		const dataObject = {
			setEventType: 'click',
			setUISortOrder: data.categoryRank,
			setContentCategory: data.category,
			setEventSubtype: 'view-more-results',
			setEventSubcategory: `Results:${this.getSubCategory()}`,
			setEventValue: SearchAnalytics.pagination.label,
			setEventCount: data.page,
		};
		this.logAnalytics(dataObject, false);
		this.sendToIngest();
	}
	/**
	 * We need to transpose the assetCategory
	 * returned from the server to the form ingests wants.
	 * Example camelCase - > Camel Case
	 * @param {*} category
	 * @param {*} data
	 */
	transposeAssetCategory(category: string, data: ResultItem) {
		let assetCategory = category;

		if (data.attributes && data.attributes.assetCategory) {
			assetCategory = data.attributes.assetCategory;
		} else if (data.attributes && data.attributes.data && data.attributes.data.type) {
			assetCategory = data.attributes.data.type;
		}

		return assetCategory;
	}

	/**
	 * Get the asset subcategory from the result item
	 * @param data ResultItem
	 */
	getAssetSubCategory(data: ResultItem): string | undefined {
		let assetSubCategory = data.attributes && data.attributes.assetSubCategory;
		const libraryRole = data.attributes && data.attributes.libraryRole;

		if (data.attributes && data.attributes.assetCategory === 'libraries' && libraryRole === 'Brand') {
			if (data.assetType === 'library') {
				assetSubCategory = 'Brand';
			} else if (data.assetType === 'libraryElement') {
				assetSubCategory = 'BrandElement';
			}
		}
		return assetSubCategory;
	}

	/**
	 * Capitalize the first char of each word
	 * @param {*} str
	 */
	toTitleCase(str: string) {
		const title = this.decamelize(str);
		return title
			.toLowerCase()
			.split(' ')
			.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
			.join(' ');
	}

	/**
	 * Convert from camel case to two words
	 * @param {*} str
	 */
	decamelize(str: string) {
		return str
			.replace(/([a-z\d])([A-Z])/g, '$1 $2')
			.replace(/([A-Z]+)([A-Z][a-z\d]+)/g, '$1 $2')
			.toLowerCase();
	}

	/**
	 * Add asset type
	 * @param {*} metric
	 * @param {*} data
	 */
	addAssetType(metric: Record<string, any>, data: any) {
		if (data && data.assetType) {
			let { assetType } = data;
			const {
				attributes: { ownershipType },
			} = data;

			if (assetType === 'review' && ownershipType === 'sharedWithYou') {
				assetType = 'sharedForReview';
			}
			(metric as Record<string, any>).setContentMimeType = assetType;
		}
	}

	/**
	 * Return the subCategory if not explicitly specified
	 * in the SDM. This probably will be the current state
	 * of the top/left nav bar.
	 */
	getSubCategory(options?: { autocomplete?: boolean }) {
		let subCategory = '';
		if (options?.autocomplete) {
			subCategory = 'AutoComplete';
		} else {
			subCategory = data[SearchAnalytics.tabTitle];
			if (SearchAnalytics.tabTitle === SEARCH_TABS_TO_LOCALIZED_STRINGS_MAP.stockMarketplace) {
				subCategory = data[SEARCH_TABS_TO_LOCALIZED_STRINGS_MAP.stock];
			}
		}
		return subCategory;
	}

	/**
	 * Determine if it's an auto suggestion or history
	 */
	isAutoSuggestion() {
		let isAutoSuggestion = false;

		if (SearchAnalytics.dropDownData.searchAutoComplete) {
			SearchAnalytics.dropDownData.searchAutoComplete.forEach((item) => {
				if (item === SearchAnalytics.keyword || item.name === SearchAnalytics.keyword) {
					isAutoSuggestion = true;
				}
			});
		}

		return isAutoSuggestion;
	}

	/**
	 * Get the index of the auto suggestion in the drop down
	 */
	getAutoSuggestionIndex() {
		let historyLen = 0;

		if (SearchAnalytics.dropDownData.searchHistory) {
			historyLen = SearchAnalytics.dropDownData.searchHistory.length;
		}

		let index = 0;

		if (SearchAnalytics.dropDownData.searchAutoComplete) {
			SearchAnalytics.dropDownData.searchAutoComplete.forEach((item, i) => {
				if (item === SearchAnalytics.keyword) {
					index = i + historyLen;
				}
			});
		}

		return index;
	}

	/**
	 * Get the number of items in dropdown
	 */
	getNumberofSearchResults() {
		let searchResults = 0;

		if (SearchAnalytics.dropDownData.searchHistory) {
			searchResults = SearchAnalytics.dropDownData.searchHistory.length;
		}

		if (SearchAnalytics.dropDownData.searchAutoComplete) {
			searchResults += SearchAnalytics.dropDownData.searchAutoComplete.length;
		}

		return searchResults;
	}

	/**
	 * Determine whether current key word comes from 'history' or 'autocomplete' or 'search' (typed and enter)
	 */
	getSearchType() {
		return SearchAnalytics.searchType;
	}

	/**
	 * [logAnalytics A utility method to set multiple values at once]
	 * @param  {[object of key/value pairs]} dataObject [events]
	 * @returns {[none]}
	 */
	logAnalytics(dataObject: Record<string, any>, sendRequestId = true) {
		// search session id - context_guid
		if (SearchAnalytics.setContextGuid && !dataObject.setContextGuid) {
			this.ingest.setDataValue('event', 'context_guid', SearchAnalytics.contextGuid);
		}
		if (this.searchHostContext && this.searchHostContext.locale && !SearchHostContext.isAuthenticated()) {
			this.ingest.setDataValue('event', 'language', this.searchHostContext.locale);
		}

		this.ingest.setDataValue('event', 'workflow', `Search|${SearchUtils.isUserOptInForLeoResponse()}`);

		if (sendRequestId && SearchAnalytics.requestId.length > 0) {
			this.ingest.setDataValue('search', 'request_guid', SearchAnalytics.requestId);
		}

		this.ingest.setDataValue('ui', 'view_type', AnalyticsUtil.clientSubcategory);

		if (SearchAnalytics.keyword && SearchAnalytics.keyword.length > 0) {
			this.ingest.setDataValue('ui', 'search_keyword', SearchAnalytics.keyword);
		}

		// Report all campaigns running
		if (
			typeof AnalyticsUtil.experiment === 'object' &&
			Object.keys(AnalyticsUtil.experiment).length > 0 &&
			!dataObject.setExpCampaignID
		) {
			const numCampaigns = Object.keys(AnalyticsUtil.experiment).length;
			let campaignsStr = '';
			let variationsStr = '';

			for (const campaign in AnalyticsUtil.experiment) {
				// If we only have one campaign just
				// send it
				if (numCampaigns === 1) {
					this.ingest.setDataValue('exp', 'campaign_id', campaign);
					this.ingest.setDataValue('exp', 'variation_id', AnalyticsUtil.experiment[campaign]);
				} else {
					// if more than one, build str that looks like this:
					// https://wiki.corp.adobe.com/display/ecoplaces/Experimentation#38728a9a-dcf6-4091-a0ce-afaee95b3f22-1731914384
					if (campaignsStr.length) {
						campaignsStr = `${campaignsStr}|`;
					}

					if (variationsStr.length) {
						variationsStr = `${variationsStr}|`;
					}

					campaignsStr = `${campaignsStr}${campaign}`;
					variationsStr = `${variationsStr}${AnalyticsUtil.experiment[campaign]}:${campaign}`;
				}
			}

			if (numCampaigns > 1) {
				this.ingest.setDataValue('exp', 'campaign_id', campaignsStr);
				this.ingest.setDataValue('exp', 'variation_id', variationsStr);
			}
		}

		dataObject.setUIViewType = dataObject.setUIViewType || AnalyticsUtil.clientSubcategory || this.getViewType();
		this.analyticsUtils.logAnalytics(dataObject);
	}

	// Save off to session storage to retain for ongoing session
	// Bug fix CCS-1980
	static saveSearchData() {
		const userId = SearchUtils.getUserIDForStorage();
		// This is being done to remove searchData key from local storage for users coming from last versions
		const searchDataInLocalStorage = WebStorage.localStorage.getItem('searchData');

		if (searchDataInLocalStorage) {
			WebStorage.localStorage.removeItem('searchData');
		}

		let searchData: any = WebStorage.sessionStorage.getItem('searchData');

		if (typeof searchData === 'object' && searchData !== null) {
			searchData[userId] = {
				// @ts-ignore
				keyword: SearchAnalytics.keyword,
				// @ts-ignore
				searchType: SearchAnalytics.searchType,
			};
		} else {
			searchData = {};
			searchData[userId] = {
				keyword: SearchAnalytics.keyword,
				searchType: SearchAnalytics.searchType,
			};
		}

		WebStorage.sessionStorage.setItem('searchData', searchData);
	}

	// Get search data for user from session storage
	static getSearchDataForUser() {
		const userId = SearchUtils.getUserIDForStorage();
		const searchData = WebStorage.sessionStorage.getItem('searchData');
		let results = {
			keyword: '',
			searchType: 'unknown',
		};

		if (typeof searchData === 'object' && searchData !== null && searchData[userId]) {
			results = searchData[userId];
		}

		return results;
	}

	// Updates and return the clickCount for a DOM target
	static updateClickCount(targetEvent, incrementBy): number {
		const { setEventType, setEventSubcategory, setEventValue, setEventSubtype, setContentCategory } = targetEvent;
		const clickCountKey = [setEventSubcategory, setEventType, setEventValue, setEventSubtype, setContentCategory]
			.filter(Boolean)
			.join('_');
		SearchAnalytics.clickCount[clickCountKey] = SearchAnalytics.clickCount[clickCountKey] || 0;
		SearchAnalytics.clickCount[clickCountKey] += incrementBy;
		return SearchAnalytics.clickCount[clickCountKey];
	}
	postSearchDelayedEventFromSRP(): void {
		this.initAnalytics();
		const dataObject = WebStorage.sessionStorage.getItem('delayedEvent');
		if (dataObject && dataObject instanceof Object) {
			dataObject.setEventType = 'click_queued';
			this.logAnalytics(dataObject, false);
			this.sendToIngest();
			WebStorage.sessionStorage.removeItem('delayedEvent');
		}
	}

	static sendCmeEventsToIngest({
		cmeEventSubType,
		cmeDataObject,
		appSapCode,
		cmeUrl,
	}: {
		cmeEventSubType: string;
		cmeDataObject: string;
		appSapCode: string;
		cmeUrl: string;
	}): void {
		const analytics = new SearchAnalytics(SearchHostContext.getContext());
		analytics.initAnalytics();
		const dataObject = {
			setEventSubcategory: 'CME-TwPModal',
			setEventValue: cmeDataObject,
			setEventType: 'click',
			setContentAction: cmeUrl,
			setEventSubtype: 'CME',
			setContentCategory: cmeEventSubType,
			setContentName: appSapCode,
		};
		analytics.logAnalytics(dataObject);
		analytics.sendToIngest();
	}
}
SearchAnalytics.prompt = '';
SearchAnalytics.keyword = '';
SearchAnalytics.sliceValue = 23;
SearchAnalytics.contextGuid = '';
SearchAnalytics.setContextGuid = true;
SearchAnalytics.currentImage = {};
SearchAnalytics.searchHistoryLen = 0;
SearchAnalytics.requestId = '';
SearchAnalytics.tabTitle = 'app.search.tab.all';
SearchAnalytics.cleared = false;
SearchAnalytics.searchType = '';
SearchAnalytics.history = 'history';
SearchAnalytics.autoComplete = 'autocomplete';
SearchAnalytics.currentSearch = 'search';
SearchAnalytics.pagination = {
	label: '',
};
